import type { NotificationResponse } from '@core/types';
import { articleApiErrorMessage } from '@/app/shared/blog/article-errors';
import { validateArticleImageFiles } from '@/app/shared/blog/article-image-validation';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getCsrfToken, getSession, signOut } from 'next-auth/react';
import { getStoredStudentYear } from '@/config/student-year';
import type {
  ContactInquiry,
  ContactInquiryDetailResponse,
  ContactInquiryListParams,
  ContactInquiryListResponse,
  CreateContactMessagePayload,
  ContactSettings,
  ContactSettingsResponse,
  UpdateContactSettingsPayload,
} from '@/app/shared/contact-inquiries/types';
import type {
  ArticleDetailResponse,
  ArticleDetails,
  ArticleApiListResponse,
  ArticleCategory,
  ArticleCategoryLookupResponse,
  ArticleLikeResponse,
  ArticleListParams,
  ArticleListResponse,
  ArticleMutationPayload,
  ArticleMutationResponse,
  ArticlePatchPayload,
  StorageFileUploadResponse,
} from '@/app/shared/blog/types';
import type {
  AdminUser,
  CreateUserPayload,
  UpdateUserPasswordPayload,
  UpdateUserPayload,
  UserListParams,
  UserListResponse,
  UserMutationResponse,
  UserOption,
  UserProfileResponse,
} from '@/app/shared/users/types';
import type {
  AljexCompanyListParams,
  AljexCompanyListResponse,
  CreateCustomerPayload,
  CustomerListParams,
  CustomerListResponse,
  CustomerMutationResponse,
  UpdateCustomerPayload,
} from '@/app/shared/customers/types';
import { toCustomerMutationError } from '@/app/shared/customers/customer-errors';
import type { ShipmentMutationPayload } from '@/app/shared/shipments/types';
import type {
  DashboardFilters,
  DocumentGapKey,
  DocumentGapResponse,
  DashboardResponse,
  PublicInternalDashboardResponse,
} from '@/app/shared/admin-dashboard/dashboard/types';

export const baseURL =
  (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '') + '/en';

export const axiosInstance = axios.create({
  baseURL: baseURL,
  withCredentials: false,
  timeout: 30_000,
});

// Public (no-auth) client for endpoints like request-reset, reset-password, register, login
export const axiosPublic = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 30_000,
});

export const axiosInstanceWithoutTimer = axios.create({
  baseURL,
  withCredentials: false,
});

const SESSION_TOKEN_CACHE_TTL = 60_000;
const TERMINAL_REFRESH_STATUSES = new Set([400, 401, 403, 422]);
type BrowserSession = Awaited<ReturnType<typeof getSession>>;
type SessionRefreshResult =
  | BrowserSession
  | { error: 'RefreshAccessTokenError' }
  | null;
let sessionTokenCache: { token?: string; expiresAt: number } | null = null;
let sessionTokenRequest: Promise<string | undefined> | null = null;
let sessionRefreshRequest: ReturnType<typeof requestSessionRefresh> | null =
  null;
const API_CACHE_TTL = 60_000;
let profileCache: { data: any; expiresAt: number } | null = null;
let profileRequest: Promise<any> | null = null;
let notificationsCache: {
  data: NotificationResponse;
  expiresAt: number;
} | null = null;
let notificationsRequest: Promise<NotificationResponse> | null = null;

function isDevAccessToken(token?: string) {
  return (
    process.env.NODE_ENV !== 'production' && token === 'dev-admin-access-token'
  );
}

function isRefreshTokenExpiredSession(
  session: SessionRefreshResult
): session is { error: 'RefreshAccessTokenError' } {
  return (
    !!session &&
    typeof session === 'object' &&
    'error' in session &&
    session.error === 'RefreshAccessTokenError'
  );
}

async function getCachedSessionToken() {
  if (typeof window === 'undefined') return undefined;

  const now = Date.now();
  if (sessionTokenCache && sessionTokenCache.expiresAt > now) {
    return sessionTokenCache.token;
  }

  if (!sessionTokenRequest) {
    sessionTokenRequest = getSession()
      .then((session) => {
        const token = session?.user?.accessToken;
        const accessTokenExpiry = session?.user?.accessTokenExpiry;
        const safeTtl =
          typeof accessTokenExpiry === 'number'
            ? Math.max(
                0,
                Math.min(
                  SESSION_TOKEN_CACHE_TTL,
                  accessTokenExpiry - Date.now() - 15_000
                )
              )
            : SESSION_TOKEN_CACHE_TTL;

        sessionTokenCache = {
          token,
          expiresAt: Date.now() + safeTtl,
        };
        return token;
      })
      .finally(() => {
        sessionTokenRequest = null;
      });
  }

  return sessionTokenRequest;
}

function cacheSessionToken(session: BrowserSession) {
  const token = session?.user?.accessToken;
  const accessTokenExpiry = session?.user?.accessTokenExpiry;
  const safeTtl =
    typeof accessTokenExpiry === 'number'
      ? Math.max(
          0,
          Math.min(
            SESSION_TOKEN_CACHE_TTL,
            accessTokenExpiry - Date.now() - 15_000
          )
        )
      : SESSION_TOKEN_CACHE_TTL;

  sessionTokenCache = {
    token,
    expiresAt: Date.now() + safeTtl,
  };
}

async function requestSessionRefresh(): Promise<SessionRefreshResult> {
  const csrfToken = await getCsrfToken();
  if (!csrfToken) return null;

  const response = await fetch('/api/auth/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      csrfToken,
      data: { forceRefresh: true },
    }),
  });

  if (!response.ok) {
    return TERMINAL_REFRESH_STATUSES.has(response.status)
      ? { error: 'RefreshAccessTokenError' }
      : null;
  }

  return (await response.json()) as BrowserSession;
}

async function refreshSessionOnce() {
  if (!sessionRefreshRequest) {
    sessionRefreshRequest = requestSessionRefresh().finally(() => {
      sessionRefreshRequest = null;
    });
  }

  return sessionRefreshRequest;
}

async function redirectToSignIn() {
  const session = await getSession();
  if (!isDevAccessToken(session?.user?.accessToken)) {
    await signOut({ callbackUrl: '/auth/sign-in' });
  }
}

async function retryAfterUnauthorized(
  error: AxiosError,
  client: AxiosInstance,
  attachAccessToken = true
) {
  const original = error.config as RetryableRequest | undefined;

  if (!original || original._retry || typeof window === 'undefined') {
    return Promise.reject(error);
  }

  original._retry = true;
  sessionTokenCache = null;

  const currentSession = await getSession();
  const requestHeaders = original.headers as any;
  const authorizationHeader = (
    typeof requestHeaders?.get === 'function'
      ? requestHeaders.get('Authorization')
      : (requestHeaders?.Authorization ?? requestHeaders?.authorization)
  ) as string | undefined;
  const failedAccessToken = authorizationHeader?.startsWith('Bearer ')
    ? authorizationHeader.slice(7)
    : undefined;
  const currentAccessToken = currentSession?.user?.accessToken;

  // Another 401 handler may already have rotated the tokens. Reuse its new
  // access token instead of rotating the refresh token a second time.
  if (
    currentAccessToken &&
    failedAccessToken &&
    currentAccessToken !== failedAccessToken
  ) {
    cacheSessionToken(currentSession);
    if (attachAccessToken) {
      original.headers = original.headers ?? {};
      (original.headers as any).Authorization = `Bearer ${currentAccessToken}`;
    }
    return client(original);
  }

  let refreshedSession: SessionRefreshResult;
  try {
    refreshedSession = await refreshSessionOnce();
  } catch {
    // Keep the authenticated session on temporary network/NextAuth failures.
    return Promise.reject(error);
  }

  if (isRefreshTokenExpiredSession(refreshedSession)) {
    await redirectToSignIn();
    return Promise.reject(error);
  }

  if (!refreshedSession) {
    // A temporary refresh/network failure must not destroy the session. The
    // next request can try refreshing again.
    return Promise.reject(error);
  }

  const newAccessToken = refreshedSession?.user?.accessToken;
  if (!newAccessToken) {
    // A temporary refresh/network failure must not destroy the session. The
    // next request can try refreshing again.
    return Promise.reject(error);
  }

  cacheSessionToken(refreshedSession);
  if (attachAccessToken) {
    original.headers = original.headers ?? {};
    (original.headers as any).Authorization = `Bearer ${newAccessToken}`;
  }
  return client(original);
}

/**
 * Browser client for authenticated Next.js proxy routes.
 *
 * Those routes keep the access token server-side, but a backend 401 is still
 * returned to the browser. Retrying through this client refreshes the NextAuth
 * session first, then repeats the proxy request with the newly-issued cookie.
 */
export const axiosAuthenticatedProxy = axios.create({
  timeout: 30_000,
});

axiosAuthenticatedProxy.interceptors.response.use(
  async (response) => {
    // Mutation callers deliberately accept validation errors as responses.
    // Handle 401 here too so validateStatus cannot bypass token refresh.
    if (response.status === 401) {
      const error = new AxiosError(
        'Request failed with status code 401',
        AxiosError.ERR_BAD_REQUEST,
        response.config,
        response.request,
        response
      );
      return retryAfterUnauthorized(error, axiosAuthenticatedProxy, false);
    }

    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      return retryAfterUnauthorized(error, axiosAuthenticatedProxy, false);
    }

    return Promise.reject(error);
  }
);

export interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

axiosInstance.interceptors.request.use(
  async (config) => {
    if (typeof window !== 'undefined') {
      const token = await getCachedSessionToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }

      const studentYear = getStoredStudentYear();
      if (studentYear) {
        config.headers = config.headers ?? {};
        (config.headers as any).student_year = studentYear;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      return retryAfterUnauthorized(error, axiosInstance);
    }

    return Promise.reject(error);
  }
);

axiosInstanceWithoutTimer.interceptors.request.use(
  async (config) => {
    if (typeof window !== 'undefined') {
      const session = await getSession();
      const token = session?.user?.accessToken;
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);
axiosInstanceWithoutTimer.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      return retryAfterUnauthorized(error, axiosInstanceWithoutTimer);
    }

    return Promise.reject(error);
  }
);

// lookup.api.ts
import type {
  UserLookupFlags,
  UserLookupKey,
  UserLookupMap,
} from '@core/types/lookup';

type LookupApiResponse<T> =
  | T
  | {
      data: T;
      success?: boolean;
    };

function unwrapLookupResponse<T>(payload: LookupApiResponse<T>): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data;
  }

  return payload;
}

// 1) Sen xohlagan format: params: { gender: true }
export async function getUserLookups<const F extends UserLookupFlags>(
  flags: F,
  extraParams?: Record<string, any>,
  config?: { signal?: AbortSignal }
) {
  const params = { ...flags, ...(extraParams ?? {}) };
  try {
    if (typeof window !== 'undefined') {
      const res = await axiosAuthenticatedProxy.get<
        LookupApiResponse<Pick<UserLookupMap, keyof F & UserLookupKey>>
      >('/api/lookup/index', { params, signal: config?.signal });
      return unwrapLookupResponse(res.data);
    }
  } catch {
    if (typeof window !== 'undefined') {
      try {
        const res = await axiosAuthenticatedProxy.get<
          LookupApiResponse<Pick<UserLookupMap, keyof F & UserLookupKey>>
        >('/api/bids/lookups', { params, signal: config?.signal });
        return unwrapLookupResponse(res.data);
      } catch {
        // Fallback to direct axiosInstance if proxy fails
      }
    }
  }

  const res = await axiosInstance.get<
    LookupApiResponse<Pick<UserLookupMap, keyof F & UserLookupKey>>
  >('/v1/lookup/index', { params, signal: config?.signal });
  return unwrapLookupResponse(res.data);
}

// 2) Qo‘shimcha qulay variant: array berasan, ichidan params yasab beradi
export async function getUserLookupsByKeys<
  const K extends readonly UserLookupKey[],
>(keys: K) {
  const params = keys.reduce(
    (acc, key) => {
      (acc as any)[key] = 1;
      return acc;
    },
    {} as Record<K[number], true>
  );

  return getUserLookups(params as any);
}

const contactInquiriesProxyPath = '/api/contact/inquiries';
const contactInquiriesApiPath = '/v1/contact/inquiries';
const contactSettingsProxyPath = '/api/contact/settings';
const contactSettingsApiPath = '/v1/settings';
const contactInquiryTypeLookupKey = 'contact-inquiry-type';
const articlesProxyPath = '/api/blog/articles';
const blogCategoriesProxyPath = '/api/blog/articles/lookups';
const articlesApiPath = '/v1/articles';
const blogPostsApiPath = '/v1/blog-posts';
const storageFilesApiPath = '/v1/storage-files/upload';
const usersProxyPath = '/api/users';
const usersApiPath = '/v1/users';
const customersProxyPath = '/api/customers';
const customersApiPath = '/v1/customers';
const aljexCompaniesProxyPath = '/api/aljex-companies';
const aljexCompaniesApiPath = '/v1/aljex-companies';
const shipmentsApiPath = '/v1/shipments';
const bidsApiPath = '/v1/bids';
const dashboardProxyPath = '/api/dashboard';
const publicInternalDashboardProxyPath = '/api/internal-dashboard/data';

type QueryParamValue = string | number | null | undefined;

/**
 * Drops empty filters so a request never sends `search=&role=&status=`.
 * Only the parameters the user actually chose reach the API.
 */
export function omitEmptyParams(params: Record<string, QueryParamValue>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined && value !== null && String(value).trim() !== ''
    )
  );
}

export async function getDashboard(
  filters: DashboardFilters,
  signal?: AbortSignal
) {
  const response = await axiosAuthenticatedProxy.get<DashboardResponse>(
    dashboardProxyPath,
    {
      params: omitEmptyParams({
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        date_field: filters.dateField,
        status: filters.status,
        source: filters.source,
        mode: filters.mode,
        customer_id: filters.customerId,
        contact_type: filters.contactType,
        bid_status: filters.bidStatus,
        search: filters.search,
        limit: filters.limit,
      }),
      signal,
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'The dashboard response is invalid.'
    );
  }

  return response.data.data;
}

export async function getDashboardDocumentGaps(
  gap: DocumentGapKey,
  filters: DashboardFilters,
  page: number,
  signal?: AbortSignal
) {
  const response = await axiosAuthenticatedProxy.get<DocumentGapResponse>(
    '/api/dashboard/document-gaps',
    {
      params: omitEmptyParams({
        gap,
        page,
        limit: 20,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        date_field: filters.dateField,
        status: filters.status,
        source: filters.source,
        mode: filters.mode,
        customer_id: filters.customerId,
        search: filters.search,
      }),
      signal,
    }
  );
  if (!response.data.success || !Array.isArray(response.data.data?.items)) {
    throw new Error(
      response.data.message || 'Document gaps could not be loaded.'
    );
  }
  return response.data.data;
}

export async function requestDashboardDocumentGapsFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<DocumentGapResponse>('/v1/dashboard/document-gaps', {
    params: Object.fromEntries(params),
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export type PublicInternalDashboardFilters = Partial<
  Pick<DashboardFilters, 'dateFrom' | 'dateTo' | 'dateField' | 'limit'>
>;

export async function getPublicInternalDashboard(
  filters: PublicInternalDashboardFilters,
  signal?: AbortSignal
) {
  const response = await axios.get<PublicInternalDashboardResponse>(
    publicInternalDashboardProxyPath,
    {
      params: omitEmptyParams({
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        date_field: filters.dateField,
        limit: filters.limit,
      }),
      signal,
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'The internal dashboard response is invalid.'
    );
  }

  return response.data.data;
}

type ContactInquiryLookupOption = {
  value: string | number;
  label: string;
};

type ContactInquiryLookupResponse = {
  data?: Record<string, ContactInquiryLookupOption[]>;
  success?: boolean;
} & Record<string, unknown>;

export async function getContactInquiries(
  params: ContactInquiryListParams,
  signal?: AbortSignal
) {
  const response =
    await axiosAuthenticatedProxy.get<ContactInquiryListResponse>(
      contactInquiriesProxyPath,
      {
        params: omitEmptyParams({
          page: params.page,
          per_page: params.perPage,
          sort: params.sort,
          type: params.type === 'all' ? '' : params.type,
          search: params.search,
        }),
        signal,
      }
    );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error('The contact inquiries response is invalid.');
  }

  return response.data;
}

export async function getContactInquiry(
  inquiryId: number,
  signal?: AbortSignal
): Promise<ContactInquiry> {
  const response =
    await axiosAuthenticatedProxy.get<ContactInquiryDetailResponse>(
      `${contactInquiriesProxyPath}/${inquiryId}`,
      { signal }
    );

  if (!response.data.success || !response.data.data) {
    throw new Error('The contact inquiry response is invalid.');
  }

  return response.data.data;
}

export async function getContactInquiryTypeLookups(signal?: AbortSignal) {
  const response =
    await axiosAuthenticatedProxy.get<ContactInquiryLookupResponse>(
      `${contactInquiriesProxyPath}/lookups`,
      {
        params: { [contactInquiryTypeLookupKey]: 1 },
        signal,
      }
    );
  const lookupData =
    response.data.data ??
    (response.data as Record<string, ContactInquiryLookupOption[]>);

  return (
    lookupData[contactInquiryTypeLookupKey] ??
    lookupData.contact_inquiry_type ??
    lookupData.contactInquiryType ??
    []
  );
}

export async function getContactSettings(
  signal?: AbortSignal
): Promise<ContactSettings> {
  const response = await axiosAuthenticatedProxy.get<ContactSettingsResponse>(
    contactSettingsProxyPath,
    { signal }
  );

  if (
    !response.data.success ||
    !response.data.data ||
    typeof response.data.data !== 'object'
  ) {
    throw new Error('The contact settings response is invalid.');
  }

  return response.data.data;
}

export async function updateContactSettings(
  payload: UpdateContactSettingsPayload,
  signal?: AbortSignal
): Promise<ContactSettings> {
  const response = await axiosAuthenticatedProxy.patch<ContactSettingsResponse>(
    contactSettingsProxyPath,
    payload,
    { signal }
  );

  if (
    !response.data.success ||
    !response.data.data ||
    typeof response.data.data !== 'object'
  ) {
    throw new Error('The contact settings response is invalid.');
  }

  return response.data.data;
}

export async function requestContactInquiriesFromApi({
  accessToken,
  inquiryId,
  params,
}: {
  accessToken: string;
  inquiryId?: number;
  params?: URLSearchParams;
}) {
  return axiosInstance.get<unknown>(
    inquiryId
      ? `${contactInquiriesApiPath}/${inquiryId}`
      : contactInquiriesApiPath,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: params ? Object.fromEntries(params) : undefined,
      validateStatus: () => true,
    }
  );
}

export async function requestPublicContactLookupsFromApi(
  params: URLSearchParams
) {
  return axiosPublic.get<unknown>('/v1/public/lookup', {
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

export type FreightShipperQuotePayload = {
  type: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  origin_state: string;
  destination_state: string;
  equipment_type: string;
  approximate_weight_or_volume: string;
  target_pickup_date: string;
  message: string;
};

export type WarehouseShipperQuotePayload = {
  type: number;
  warehouse_location_needed: string;
  storage_type: string;
  storage_duration: string;
  estimated_space_needed: string;
  number_of_pallets_or_units: string;
  requested_start_date: string;
  inbound_frequency: string;
  customer_type: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  subject: string;
  special_handling_requirements: string[];
  value_added_services_needed: string[];
  freight_description: string;
  additional_notes: string;
  message: string;
};

export type ShipperQuotePayload =
  | FreightShipperQuotePayload
  | WarehouseShipperQuotePayload;

export async function createPublicShipperQuote(payload: ShipperQuotePayload) {
  return axiosPublic.post<unknown>('/v1/contact/shipper-quote', payload, {
    validateStatus: () => true,
  });
}

export async function createPublicContactMessage(
  payload: CreateContactMessagePayload
) {
  return axiosPublic.post<unknown>('/v1/contact/message', payload, {
    validateStatus: () => true,
  });
}

export async function requestPublicShipmentFromApi(pro: number) {
  return axiosPublic.get<unknown>(`/v1/public/shipments/${pro}`, {
    validateStatus: () => true,
  });
}

export async function refetchPublicShipmentTrackingFromApi(pro: number) {
  return axiosPublic.post<unknown>(
    `/v1/aljex-shipments/${pro}/macropoint/refetch`,
    undefined,
    {
      validateStatus: () => true,
    }
  );
}

export type PublicShipmentBidPayload = {
  first_name: string;
  last_name: string;
  email: string;
  amount: number;
  currency: 'USD';
  company_name: string;
  phone: string;
  message: string;
};

export async function createPublicShipmentBid(
  pro: number,
  payload: PublicShipmentBidPayload
) {
  return axiosPublic.post<unknown>(
    `/v1/public/shipments/${pro}/bids`,
    payload,
    { validateStatus: () => true }
  );
}

export type PublicShipment = {
  pro: number;
  status: string;
  /**
   * Optional until the logistics API ships the Book Now contract. New values:
   * AVAILABLE, BOOK_PENDING, ON_HOLD, and BOOKED.
   */
  availability_status?: string | null;
  booking_status?: string | null;
  can_bid?: boolean | null;
  can_book_now?: boolean | null;
  pickup: { city: string | null; state: string | null };
  destination: { city: string | null; state: string | null };
  freight: {
    pieces: number | null;
    weight: number | null;
    pallets: number | null;
    description: string | null;
    equipment_type: {
      code: string | null;
      description: string | null;
    };
    temperature: number | null;
    hazmat: boolean | null;
  };
  estimated_times: {
    pickup_at: string | number | null;
    delivery_at: string | number | null;
  };
  price: number | null;
  miles: number | null;
  last_updated_at: string | number | null;
};

export type PublicShipmentListParams = {
  page: number;
  perPage: number;
  search?: string;
  pickupCity?: string;
  pickupState?: string;
  destinationCity?: string;
  destinationState?: string;
  pickupDateFrom?: string;
  pickupDateTo?: string;
  sort: string;
};

export type PublicShipmentPagination = {
  total: number;
  page: number;
  per_page: number;
  page_count: number;
};

export type PublicShipmentListResponse = {
  data: PublicShipment[];
  _meta: { pagination: PublicShipmentPagination };
  success: boolean;
};

export async function getPublicShipments(
  params: PublicShipmentListParams,
  signal?: AbortSignal
) {
  const response =
    await axiosAuthenticatedProxy.get<PublicShipmentListResponse>(
      '/api/public/shipments',
      {
        params: omitEmptyParams({
          page: params.page,
          per_page: params.perPage,
          search: params.search,
          pickup_city: params.pickupCity,
          pickup_state: params.pickupState,
          destination_city: params.destinationCity,
          destination_state: params.destinationState,
          pickup_date_from: params.pickupDateFrom,
          pickup_date_to: params.pickupDateTo,
          sort: params.sort,
        }),
        signal,
      }
    );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error('The public shipments response is invalid.');
  }

  return response.data;
}

export async function requestAvailableLoadsFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<unknown>('/v1/public/shipments', {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

export type PublicLookupOption = {
  value: string | number;
  label: string;
};

export type PublicContactLookups = {
  'contact-inquiry-type': PublicLookupOption[];
  'preferred-contact-method': PublicLookupOption[];
};

type PublicContactLookupResponse = {
  data: Partial<PublicContactLookups>;
  success: boolean;
};

export async function getPublicContactLookups(signal?: AbortSignal) {
  const response = await axios.get<PublicContactLookupResponse>(
    `${contactInquiriesProxyPath}/lookups`,
    {
      params: {
        'contact-inquiry-type': 1,
        'preferred-contact-method': 1,
      },
      signal,
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error('The public lookup response is invalid.');
  }

  return {
    'contact-inquiry-type': response.data.data['contact-inquiry-type'] ?? [],
    'preferred-contact-method':
      response.data.data['preferred-contact-method'] ?? [],
  } satisfies PublicContactLookups;
}

export async function requestContactSettingsFromApi({
  accessToken,
  method,
  payload,
}: {
  accessToken: string;
  method: 'GET' | 'PATCH';
  payload?: UpdateContactSettingsPayload;
}) {
  return axiosInstance.request<unknown>({
    url: contactSettingsApiPath,
    method,
    data: payload,
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function getArticles(
  params: ArticleListParams,
  signal?: AbortSignal
) {
  const response = await axiosAuthenticatedProxy.get<ArticleApiListResponse>(
    articlesProxyPath,
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        sort: params.sort,
        search: params.search,
        category: params.category,
      }),
      signal,
    }
  );

  const items = Array.isArray(response.data.data)
    ? response.data.data
    : response.data.data?.items;
  const categories = Array.isArray(response.data.data)
    ? []
    : (response.data.data?.categories ?? []);

  if (!response.data.success || !Array.isArray(items)) {
    throw new Error('The articles response is invalid.');
  }

  return { ...response.data, data: items, categories };
}

export async function getArticle(
  articleSlug: string,
  signal?: AbortSignal
): Promise<ArticleDetails> {
  const response = await axiosAuthenticatedProxy.get<ArticleDetailResponse>(
    `${articlesProxyPath}/${encodeURIComponent(articleSlug)}`,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error('The article response is invalid.');
  }

  return response.data.data;
}

export async function getBlogCategories(signal?: AbortSignal) {
  const response =
    await axiosAuthenticatedProxy.get<ArticleCategoryLookupResponse>(
      blogCategoriesProxyPath,
      { signal }
    );

  const categories = response.data.data?.['blog-category'];
  if (!response.data.success || !Array.isArray(categories)) {
    throw new Error('The blog category lookup response is invalid.');
  }

  const validCategories = categories.filter(
    (category): category is ArticleCategory =>
      Number.isInteger(category?.value) &&
      category.value > 0 &&
      typeof category.label === 'string' &&
      category.label.trim().length > 0
  );

  if (validCategories.length === 0) {
    throw new Error('The blog category lookup is empty.');
  }

  return validCategories;
}

/** Public landing-page feed. Article reads do not require a dashboard session. */
export async function getPublicArticles(
  params: ArticleListParams,
  signal?: AbortSignal
) {
  const response = await axiosPublic.get<ArticleApiListResponse>(
    articlesApiPath,
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        sort: params.sort,
        search: params.search,
        category: params.category,
      }),
      signal,
    }
  );

  const items = Array.isArray(response.data.data)
    ? response.data.data
    : response.data.data?.items;
  const categories = Array.isArray(response.data.data)
    ? []
    : (response.data.data?.categories ?? []);

  if (!response.data.success || !Array.isArray(items)) {
    throw new Error('The public articles response is invalid.');
  }

  return { ...response.data, data: items, categories };
}

/** Public landing-page article detail. */
export async function getPublicArticle(
  articleSlug: string,
  signal?: AbortSignal
): Promise<ArticleDetails> {
  const response = await axiosPublic.get<ArticleDetailResponse>(
    `${articlesApiPath}/${encodeURIComponent(articleSlug)}`,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error('The public article response is invalid.');
  }

  return response.data.data;
}

export async function createArticle(payload: ArticleMutationPayload) {
  const response = await axiosAuthenticatedProxy.post<ArticleMutationResponse>(
    articlesProxyPath,
    payload
  );

  if (!response.data.success) {
    throw new Error(
      articleApiErrorMessage(response.data, 'The article could not be created.')
    );
  }

  return response.data;
}

export async function updateArticle(
  articleSlug: string,
  payload: ArticlePatchPayload
) {
  const response = await axiosAuthenticatedProxy.patch<ArticleMutationResponse>(
    `${articlesProxyPath}/${encodeURIComponent(articleSlug)}`,
    payload
  );

  if (!response.data.success) {
    throw new Error(
      articleApiErrorMessage(response.data, 'The article could not be updated.')
    );
  }

  return response.data;
}

export async function uploadArticleImages({
  articleId,
  purpose,
  files,
}: {
  articleId: number;
  purpose: 'cover' | 'gallery';
  files: File[];
}) {
  const validationError = validateArticleImageFiles(files);
  if (validationError) throw new Error(validationError);
  const data = new FormData();
  data.set('attributes[object_type]', 'blog_post');
  data.set('attributes[object_id]', String(articleId));
  data.set('attributes[purpose]', purpose);
  files.forEach((file) => data.append('files', file, file.name));

  const response =
    await axiosAuthenticatedProxy.post<StorageFileUploadResponse>(
      '/api/blog/storage-files',
      data
    );

  if (!response.data.success || !response.data.data) {
    throw new Error(
      articleApiErrorMessage(response.data, 'The image could not be uploaded.')
    );
  }

  return response.data.data;
}

export async function deleteArticleImage({
  articleId,
  fileId,
  purpose,
}: {
  articleId: number;
  fileId: string;
  purpose: 'cover' | 'gallery';
}) {
  const response = await axiosAuthenticatedProxy.delete<{
    success: boolean;
    message?: string;
  }>(`/api/blog/storage-files/${encodeURIComponent(fileId)}`, {
    data: {
      object_type: 'blog_post',
      object_id: articleId,
      purpose,
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.message || 'The image could not be deleted.');
  }
}

export async function deleteArticle(articleSlug: string) {
  const response =
    await axiosAuthenticatedProxy.delete<ArticleMutationResponse>(
      `${articlesProxyPath}/${encodeURIComponent(articleSlug)}`
    );

  if (!response.data.success) {
    throw new Error(
      response.data.message || 'The article could not be deleted.'
    );
  }

  return response.data;
}

export async function likePublicArticle(articleSlug: string) {
  const response = await axiosPublic.post<ArticleLikeResponse>(
    `${blogPostsApiPath}/${encodeURIComponent(articleSlug)}/like`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'The article could not be liked.');
  }

  const likesCount =
    response.data.data?.likes_count ?? response.data.likes_count ?? null;

  return typeof likesCount === 'number' ? likesCount : null;
}

export async function requestArticlesFromApi({
  accessToken,
  method,
  articleSlug,
  params,
  payload,
}: {
  accessToken: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  articleSlug?: string;
  params?: URLSearchParams;
  payload?: ArticleMutationPayload | ArticlePatchPayload;
}) {
  if ((method === 'PATCH' || method === 'DELETE') && !articleSlug) {
    throw new Error(`An article slug is required for ${method}.`);
  }

  const encodedSlug = articleSlug ? encodeURIComponent(articleSlug) : undefined;
  const url =
    method === 'GET'
      ? encodedSlug
        ? `${articlesApiPath}/${encodedSlug}`
        : articlesApiPath
      : method === 'POST'
        ? blogPostsApiPath
        : `${blogPostsApiPath}/${encodedSlug}`;

  return axiosInstance.request<unknown>({
    url,
    method,
    data: payload,
    params: params ? Object.fromEntries(params) : undefined,
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function requestBlogCategoriesFromApi({
  accessToken,
}: {
  accessToken: string;
}) {
  return axiosInstance.get<unknown>('/v1/lookup/index', {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: { 'blog-category': 1 },
    validateStatus: () => true,
  });
}

export async function requestStorageFileUploadFromApi({
  accessToken,
  data,
}: {
  accessToken: string;
  data: FormData;
}) {
  return axiosInstance.post<unknown>(storageFilesApiPath, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function requestStorageFileDeleteFromApi({
  accessToken,
  fileId,
  payload,
}: {
  accessToken: string;
  fileId: string;
  payload: {
    object_type: 'blog_post';
    object_id: number;
    purpose: 'cover' | 'gallery';
  };
}) {
  return axiosInstance.delete<unknown>(
    `${storageFilesApiPath.replace('/upload', '')}/${encodeURIComponent(fileId)}`,
    {
      data: payload,
      headers: { Authorization: `Bearer ${accessToken}` },
      validateStatus: () => true,
    }
  );
}

export async function getUsers(params: UserListParams, signal?: AbortSignal) {
  const response = await axiosAuthenticatedProxy.get<UserListResponse>(
    usersProxyPath,
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        sort: params.sort,
        search: params.search,
        status: params.status === 'all' ? '' : params.status,
      }),
      signal,
    }
  );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error('The users response is invalid.');
  }

  return response.data;
}

export async function getUserProfile(signal?: AbortSignal): Promise<AdminUser> {
  const response = await axiosAuthenticatedProxy.get<UserProfileResponse>(
    `${usersProxyPath}/profile`,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error('The profile response is invalid.');
  }

  return response.data.data;
}

export async function enableTwoFactor() {
  const response = await axiosAuthenticatedProxy.post('/api/auth/2fa/enable');
  if (response.data?.success === false) {
    throw new Error(
      response.data?.message ||
        'Two-factor authentication could not be started.'
    );
  }
  return response.data;
}

export async function confirmTwoFactorEnable(payload: {
  challenge_token: string;
  code: string;
}) {
  const response = await axiosAuthenticatedProxy.post(
    '/api/auth/2fa/confirm-enable',
    payload
  );
  if (response.data?.success === false) {
    throw new Error(
      response.data?.message ||
        'Two-factor authentication could not be enabled.'
    );
  }
  return response.data;
}

export async function disableTwoFactor(password: string) {
  const response = await axiosAuthenticatedProxy.post('/api/auth/2fa/disable', {
    password,
  });
  if (response.data?.success === false) {
    throw new Error(
      response.data?.message ||
        'Two-factor authentication could not be disabled.'
    );
  }
  return response.data;
}

export async function createUser(payload: CreateUserPayload) {
  const response = await axiosAuthenticatedProxy.post<UserMutationResponse>(
    usersProxyPath,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'The user could not be created.');
  }

  return response.data;
}

export async function updateUser(userId: number, payload: UpdateUserPayload) {
  const response = await axiosAuthenticatedProxy.put<UserMutationResponse>(
    `${usersProxyPath}/${userId}`,
    payload
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'The user could not be updated.');
  }

  return response.data;
}

export async function updateUserPassword(
  userId: number,
  payload: UpdateUserPasswordPayload
) {
  const response = await axiosAuthenticatedProxy.put<UserMutationResponse>(
    `${usersProxyPath}/${userId}/password`,
    payload
  );

  if (!response.data.success) {
    throw new Error(
      response.data.message || 'The password could not be updated.'
    );
  }

  return response.data;
}

export async function deleteUser(userId: number) {
  const response = await axiosAuthenticatedProxy.delete<UserMutationResponse>(
    `${usersProxyPath}/${userId}`
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'The user could not be deleted.');
  }

  return response.data;
}

type UserLookupsResponse = {
  data?: Record<string, UserOption[]>;
  success?: boolean;
} & Record<string, unknown>;

export async function getUserRoleAndStatusLookups(signal?: AbortSignal) {
  const response = await axiosAuthenticatedProxy.get<UserLookupsResponse>(
    `${usersProxyPath}/lookups`,
    {
      params: { role: 1, status: 1 },
      signal,
    }
  );

  const lookupData =
    response.data.data ?? (response.data as Record<string, UserOption[]>);

  return {
    role: Array.isArray(lookupData.role) ? lookupData.role : [],
    status: Array.isArray(lookupData.status) ? lookupData.status : [],
  };
}

export async function requestUsersFromApi({
  accessToken,
  method,
  userId,
  subPath,
  params,
  payload,
}: {
  accessToken: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  userId?: number;
  subPath?: string;
  params?: URLSearchParams;
  payload?: CreateUserPayload | UpdateUserPayload | UpdateUserPasswordPayload;
}) {
  const segments = [usersApiPath];
  if (userId !== undefined) segments.push(String(userId));
  if (subPath) segments.push(subPath);

  return axiosInstance.request<unknown>({
    url: segments.join('/'),
    method,
    data: payload,
    params: params ? Object.fromEntries(params) : undefined,
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function getCustomers(
  params: CustomerListParams,
  signal?: AbortSignal
) {
  const response = await axiosAuthenticatedProxy.get<CustomerListResponse>(
    customersProxyPath,
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        sort: params.sort,
        search: params.search,
      }),
      signal,
    }
  );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error('The customers response is invalid.');
  }

  return response.data;
}

export async function createCustomer(payload: CreateCustomerPayload) {
  const response = await axiosAuthenticatedProxy.post<CustomerMutationResponse>(
    customersProxyPath,
    payload,
    { validateStatus: () => true }
  );

  if (!response.data.success) {
    throw toCustomerMutationError(
      response.data,
      'The customer could not be created.'
    );
  }

  return response.data;
}

export async function updateCustomer(
  customerId: number,
  payload: UpdateCustomerPayload
) {
  const response =
    await axiosAuthenticatedProxy.patch<CustomerMutationResponse>(
      `${customersProxyPath}/${customerId}`,
      payload,
      { validateStatus: () => true }
    );

  if (!response.data.success) {
    throw toCustomerMutationError(
      response.data,
      'The customer could not be updated.'
    );
  }

  return response.data;
}

export async function deleteCustomer(customerId: number) {
  const response =
    await axiosAuthenticatedProxy.delete<CustomerMutationResponse>(
      `${customersProxyPath}/${customerId}`,
      { validateStatus: () => true }
    );

  if (!response.data.success) {
    throw toCustomerMutationError(
      response.data,
      'The customer could not be deleted.'
    );
  }

  return response.data;
}

export async function getAljexCompanies(
  params: AljexCompanyListParams,
  signal?: AbortSignal
) {
  const response = await axiosAuthenticatedProxy.get<AljexCompanyListResponse>(
    aljexCompaniesProxyPath,
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        search: params.search,
      }),
      signal,
    }
  );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error('The Aljex companies response is invalid.');
  }

  return response.data;
}

export async function requestCustomersFromApi({
  accessToken,
  method,
  customerId,
  params,
  payload,
}: {
  accessToken: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  customerId?: number;
  params?: URLSearchParams;
  payload?: CreateCustomerPayload | UpdateCustomerPayload;
}) {
  return axiosInstance.request<unknown>({
    url:
      customerId === undefined
        ? customersApiPath
        : `${customersApiPath}/${customerId}`,
    method,
    data: payload,
    params: params ? Object.fromEntries(params) : undefined,
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function requestShipmentsFromApi({
  accessToken,
  method = 'GET',
  pro,
  params,
  payload,
}: {
  accessToken: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  pro?: number;
  params?: URLSearchParams;
  payload?: ShipmentMutationPayload;
}) {
  return axiosInstance.request<unknown>({
    url: pro === undefined ? shipmentsApiPath : `${shipmentsApiPath}/${pro}`,
    method,
    data: payload,
    headers: { Authorization: `Bearer ${accessToken}` },
    params: params ? Object.fromEntries(params) : undefined,
    validateStatus: () => true,
  });
}

export async function requestBidsFromApi({
  accessToken,
  pro,
  params,
}: {
  accessToken: string;
  pro?: number;
  params?: URLSearchParams;
}) {
  return axiosInstance.get<unknown>(
    pro === undefined ? bidsApiPath : `${shipmentsApiPath}/${pro}/bids`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: params ? Object.fromEntries(params) : undefined,
      validateStatus: () => true,
    }
  );
}

export async function requestBidUpdateFromApi({
  accessToken,
  id,
  status,
}: {
  accessToken: string;
  id: number;
  status: number;
}) {
  return axiosInstance.put<unknown>(
    `${bidsApiPath}/${id}`,
    { status },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      validateStatus: () => true,
    }
  );
}

export async function requestDashboardFromApi({
  accessToken,
  dashboard,
  params,
}: {
  accessToken: string;
  dashboard: 'internal' | 'customer';
  params: URLSearchParams;
}) {
  return axiosInstance.get<DashboardResponse>(`/v1/dashboard/${dashboard}`, {
    params: Object.fromEntries(params),
    headers: { Authorization: `Bearer ${accessToken}` },
    validateStatus: () => true,
  });
}

export async function requestPublicInternalDashboardFromApi(
  params: URLSearchParams
) {
  return axiosPublic.get<PublicInternalDashboardResponse>(
    '/v1/public/internal-dashboard',
    {
      params: Object.fromEntries(params),
      validateStatus: () => true,
    }
  );
}

export async function requestAljexCompaniesFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<unknown>(aljexCompaniesApiPath, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

export async function requestUserLookupsFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<unknown>('/v1/lookup/index', {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

const loadboardBidsApiPath = '/v1/loadboard/bids';
const loadboardBooksApiPath = '/v1/loadboard/books';
const loadboardShipmentsApiPath = '/v1/loadboard/shipments';

export type LoadboardShipment = {
  pro: number;
  status: string;
  pickup: {
    city: string | null;
    state: string | null;
  };
  destination: {
    city: string | null;
    state: string | null;
  };
  freight: {
    pieces: number | null;
    weight: number | null;
    pallets: number | null;
    description: string | null;
    equipment_type: {
      code: string | null;
      description: string | null;
    };
    temperature: number | null;
    hazmat: boolean | null;
  };
  estimated_times: {
    pickup_at: string | null;
    delivery_at: string | null;
  };
  price: number | null;
  miles: number | null;
  last_updated_at: string | number | null;
};

export type LoadboardEntryType = {
  value: number;
  label: string;
};

export type LoadboardEntryStatus = {
  value: number;
  label: string;
};

export type CustomerLoadboardItem = {
  id: number;
  shipment: LoadboardShipment;
  type: LoadboardEntryType;
  amount: number;
  currency: string;
  message: string | null;
  status:
    | LoadboardEntryStatus
    | number
    | string
    | { value?: number | string; label?: string; id?: number | string };
  created_at: string;
  updated_at: string;
};

export type CustomerLoadboardPagination = {
  total: number;
  page: number;
  per_page: number;
  page_count: number;
};

export type CustomerLoadboardResponse = {
  data: CustomerLoadboardItem[];
  _meta: {
    pagination: CustomerLoadboardPagination;
  };
  success: boolean;
  message?: string;
};

export type CustomerLoadboardListParams = {
  page: number;
  perPage: number;
  search?: string;
  status?: string | number;
  shipmentPro?: string | number;
};

export async function requestLoadboardBidsFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<CustomerLoadboardResponse>(loadboardBidsApiPath, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

export async function requestLoadboardBooksFromApi({
  accessToken,
  params,
}: {
  accessToken: string;
  params: URLSearchParams;
}) {
  return axiosInstance.get<CustomerLoadboardResponse>(loadboardBooksApiPath, {
    headers: { Authorization: `Bearer ${accessToken}` },
    params: Object.fromEntries(params),
    validateStatus: () => true,
  });
}

export async function requestCreateLoadboardBidFromApi({
  accessToken,
  pro,
  payload,
}: {
  accessToken: string;
  pro: number;
  payload: {
    amount: number;
    currency: string;
    message?: string;
  };
}) {
  return axiosInstance.post<unknown>(
    `${loadboardShipmentsApiPath}/${pro}/bids`,
    payload,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      validateStatus: () => true,
    }
  );
}

export async function requestCreateLoadboardBookNowFromApi({
  accessToken,
  pro,
  payload,
}: {
  accessToken: string;
  pro: number;
  payload: {
    message?: string;
  };
}) {
  return axiosInstance.post<unknown>(
    `${loadboardShipmentsApiPath}/${pro}/book-now`,
    payload,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      validateStatus: () => true,
    }
  );
}

function extractPrimitiveParam(value: unknown): string | number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'object') {
    const raw =
      (value as Record<string, any>).value ?? (value as Record<string, any>).id;
    if (raw !== null && raw !== undefined) {
      if (typeof raw === 'number') return Number.isNaN(raw) ? undefined : raw;
      const trimmed = String(raw).trim();
      if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') {
        return undefined;
      }
      const num = Number(trimmed);
      return Number.isInteger(num) && /^-?\d+$/.test(trimmed) ? num : trimmed;
    }
    return undefined;
  }
  const str = String(value).trim();
  if (str === '' || str === 'undefined' || str === 'null') return undefined;
  const num = Number(str);
  return Number.isInteger(num) && /^-?\d+$/.test(str) ? num : str;
}

// Customer loadboard client API functions
export async function getCustomerBids(
  params: CustomerLoadboardListParams,
  signal?: AbortSignal
) {
  const status = extractPrimitiveParam(params.status);
  const search = extractPrimitiveParam(params.search);
  const shipmentPro = extractPrimitiveParam(params.shipmentPro);

  return axiosAuthenticatedProxy.get<CustomerLoadboardResponse>(
    '/api/customer/bids',
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        search,
        status,
        shipment_pro: shipmentPro,
      }),
      signal,
    }
  );
}

export async function getCustomerBooks(
  params: CustomerLoadboardListParams,
  signal?: AbortSignal
) {
  const status = extractPrimitiveParam(params.status);
  const search = extractPrimitiveParam(params.search);
  const shipmentPro = extractPrimitiveParam(params.shipmentPro);

  return axiosAuthenticatedProxy.get<CustomerLoadboardResponse>(
    '/api/customer/books',
    {
      params: omitEmptyParams({
        page: params.page,
        per_page: params.perPage,
        search,
        status,
        shipment_pro: shipmentPro,
      }),
      signal,
    }
  );
}

export async function createCustomerShipmentBid(
  pro: number,
  payload: {
    amount: number;
    currency?: string;
    message?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    company_name?: string;
  }
) {
  return axiosAuthenticatedProxy.post<{
    success: boolean;
    data?: unknown;
    message?: string;
  }>(`/api/customer/shipments/${pro}/bids`, payload);
}

export async function createCustomerShipmentBookNow(
  pro: number,
  payload: { message?: string }
) {
  return axiosAuthenticatedProxy.post<{
    success: boolean;
    data?: unknown;
    message?: string;
  }>(`/api/customer/shipments/${pro}/book-now`, payload);
}

export type BidStatusOption = {
  value: number | string;
  label: string;
};

export async function getShipmentBidStatuses(
  signal?: AbortSignal
): Promise<BidStatusOption[]> {
  try {
    const raw = await getUserLookups(
      { 'shipment-bid-status': 1 },
      undefined,
      signal ? { signal } : undefined
    );

    let list: unknown[] = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, any>;
      if (Array.isArray(obj['shipment-bid-status'])) {
        list = obj['shipment-bid-status'];
      } else if (obj.data && Array.isArray(obj.data['shipment-bid-status'])) {
        list = obj.data['shipment-bid-status'];
      } else if (Array.isArray(obj.data)) {
        list = obj.data;
      }
    }

    const normalized = list
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const rawItem = item as Record<string, any>;
        const value = rawItem.value ?? rawItem.id ?? rawItem.key;
        const label =
          rawItem.label ?? rawItem.name ?? rawItem.title ?? String(value ?? '');
        if (value === undefined || value === null) return null;
        const numValue = Number(value);
        return {
          value: Number.isNaN(numValue) ? value : numValue,
          label: String(label || value),
        };
      })
      .filter((item): item is BidStatusOption => Boolean(item));

    if (normalized.length > 0) {
      return normalized;
    }
  } catch (error) {
    console.warn('Failed to load shipment-bid-status lookup:', error);
  }

  return [];
}

export function resolveStatusLabel(
  status: unknown,
  statusList: BidStatusOption[] = []
): string {
  if (status && typeof status === 'object') {
    const obj = status as Record<string, any>;
    if (obj.label && typeof obj.label === 'string' && obj.label.trim() !== '') {
      return obj.label;
    }
    const val = obj.value ?? obj.id ?? obj.status;
    if (val !== undefined && val !== null) {
      const match = statusList.find(
        (s) =>
          String(s.value) === String(val) ||
          s.label.toLowerCase() === String(val).toLowerCase()
      );
      if (match) return match.label;
      return String(val);
    }
  }

  if (status !== null && status !== undefined && status !== '') {
    const match = statusList.find(
      (s) =>
        String(s.value) === String(status) ||
        s.label.toLowerCase() === String(status).toLowerCase()
    );
    if (match) return match.label;
    return String(status);
  }

  return '—';
}

export default axiosInstance;
